import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';

import InputMaskAlternative from '../../../../components/InputMaskAlternative';
import InputWithoutTitle from '../../../../components/InputWithoutTitle';
import getValidationErrors from '../../../../utils/getValidationErrors';
import useQuery from '../../../../hooks/query';
import { useCurrentCallback } from '../../../../lib/use-current-effect';
import {
  identifyPatientActivation,
  PatientActivationIdentity,
} from '../../../../services/patient/PatientActivation';

import { Container, Field, SendButton, WhatsappLinkAction } from './styles';

interface IdentificationProps {
  onIdentified: (dados: {
    identity: PatientActivationIdentity;
    cpf: string;
    birthday: string;
  }) => void;
}

interface FormProps {
  cpf: string;
  birthday: string;
}

/**
 * Passo 1: confirma quem é a pessoa antes de qualquer dado do cadastro aparecer na tela.
 *
 * ⚠️ **O back responde uma mensagem só para CPF e nascimento errados**, sem dizer qual dos
 * dois falhou: quem tem o link pode ser quem recebeu um e-mail digitado errado, e resposta
 * por campo confirmaria a ele o CPF de outra pessoa. A tela não tenta adivinhar o campo.
 */
const Identification: React.FC<IdentificationProps> = ({ onIdentified }) => {
  const formRef = useRef<FormHandles>(null);
  const query = useQuery();
  const token = useMemo(() => query.get('token'), [query]);
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [contaJaAtivada, setContaJaAtivada] = useState(false);

  useEffect(() => {
    if (!token) {
      history.push('/login');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSubmit = useCurrentCallback(
    (isCurrent) => async (data: FormProps) => {
      setLoading(true);
      formRef.current?.setErrors({});

      try {
        const schema = Yup.object().shape({
          cpf: Yup.string()
            .length(14, 'Digite um CPF válido')
            .required('O CPF é obrigatório'),
          birthday: Yup.string().required('A data de nascimento é obrigatória'),
        });

        await schema.validate(data, { abortEarly: false });

        const identity = await identifyPatientActivation({
          token: token ?? '',
          cpf: data.cpf,
          birthday: data.birthday,
        });

        if (isCurrent()) {
          setLoading(false);
          onIdentified({
            identity,
            cpf: data.cpf,
            birthday: data.birthday,
          });
        }
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
        } else if (err.response?.data?.status === 'patient_already_activated') {
          /**
           * ⚠️ **Branch por código, não por mensagem** (0.35): a conta já foi ativada, então
           * este link não cria mais senha — mas a pessoa provavelmente voltou aqui querendo
           * o WhatsApp, que é o passo que ela pode ter pulado. Em vez de um toast de erro,
           * a tela oferece o caminho.
           */
          setContaJaAtivada(true);
        } else if (err.response) {
          toast.error(err.response.data.message);
        } else {
          toast.error('Entre em contato com a equipe que fez o seu cadastro.');
        }

        setLoading(false);
      }
    },
    [onIdentified, token],
  );

  if (contaJaAtivada) {
    return (
      <Container>
        <h1>Esta conta já está ativada</h1>
        <h2>
          A senha já foi criada e o termo já foi assinado, então este link não
          tem mais o que fazer.
        </h2>
        <p>
          Se você ainda não recebe o acompanhamento pelo WhatsApp, é por aqui.
        </p>
        <WhatsappLinkAction to="/patient/whatsapp">
          Vincular meu WhatsApp
        </WhatsappLinkAction>
      </Container>
    );
  }

  return (
    <Container>
      <h1>Bem-vindo(a) à Cinco</h1>
      <h2>Confirme os seus dados para concluir o seu cadastro:</h2>
      <p>Os dados precisam ser iguais aos que a equipe de saúde cadastrou.</p>
      <Form ref={formRef} onSubmit={handleSubmit}>
        <Field>
          <label>CPF:</label>
          <InputMaskAlternative
            name="cpf"
            type="text"
            title=""
            placeholder="000.000.000-00"
            mask="999.999.999-99"
          />
        </Field>
        <Field>
          <label>Data de nascimento:</label>
          <InputWithoutTitle
            className="datepicker"
            name="birthday"
            type="date"
            max={new Date().toISOString().split('T')[0]}
          />
        </Field>
        <SendButton loading={loading} type="submit">
          Continuar
        </SendButton>
      </Form>
    </Container>
  );
};

export default Identification;
